import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SETUP_SAVE_MUTATION_KEY } from '@/core/components/reputation/setup/setup-save-status';
import { LocationsApi } from '@up2stay/up2promo-api-client';
import { toast } from 'sonner';

import {
  SettingsGroupLabel,
  SettingsGroupFooter,
  SettingsGroup,
} from '@/core/components/settings/settings-group';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import { useLocations } from '@/core/hooks/use-locations';
import { locationsQueryKeys } from '@/core/query-keys/locations-query-keys';
import { useDictionary } from '@/core/providers/dictionary-provider';

import {
  hasAutoReplyOverride,
  ReputationAutoReplySettings,
} from './reputation-auto-reply-settings';
import { SetupOverrideList } from './setup-override-list';

function useAutoReplyDict() {
  const d = useDictionary().reputation.setup.responses.autoReply;
  return {
    d,
    settingsDict: {
      enabledLabel: d.enabledLabel,
      enabledDescription: d.enabledDescription,
      minStarsLabel: d.minStarsLabel,
      minDelayLabel: d.minDelayLabel,
      autoPublishLabel: d.autoPublishLabel,
      autoPublishDescription: d.autoPublishDescription,
      excludeWithTextLabel: d.excludeWithTextLabel,
      excludeWithTextDescription: d.excludeWithTextDescription,
      excludeNegativeSentimentLabel: d.excludeNegativeSentimentLabel,
      excludeNegativeSentimentDescription:
        d.excludeNegativeSentimentDescription,
      save: d.save,
      saveSuccess: d.saveSuccess,
      saveError: d.saveError,
      guardrailsHint: d.guardrailsHint,
      delayImmediately: d.delayImmediately,
      delayMinutes: d.delayMinutes,
      delayHours: d.delayHours,
      delayDay: d.delayDay,
    },
  };
}

/** Workspace-wide automatic reply switches. */
export function ReputationSetupAutoReplyDefaults({
  workspaceId,
}: {
  workspaceId: string;
}) {
  const { d, settingsDict } = useAutoReplyDict();
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-0.5">
        <SettingsGroupLabel>{d.title}</SettingsGroupLabel>
        {d.guardrailsHint ? (
          <SettingsInfoHint label={d.title}>
            {d.guardrailsHint}
          </SettingsInfoHint>
        ) : null}
      </div>
      <SettingsGroup>
        <ReputationAutoReplySettings
          workspaceId={workspaceId}
          scope="workspace"
          dict={settingsDict}
          grouped
        />
      </SettingsGroup>
      {d.description ? (
        <SettingsGroupFooter>{d.description}</SettingsGroupFooter>
      ) : null}
    </div>
  );
}

/** Locations whose automatic replies differ from the workspace default. */
export function ReputationSetupAutoReplyOverrides({
  workspaceId,
  initialLocationId,
}: {
  workspaceId: string;
  initialLocationId?: string;
}) {
  const { d, settingsDict } = useAutoReplyDict();
  const queryClient = useQueryClient();
  const { locations } = useLocations(workspaceId, { limit: 100 });

  const removeMutation = useMutation({
    mutationKey: SETUP_SAVE_MUTATION_KEY,
    mutationFn: async (locationId: string) => {
      await LocationsApi.update({
        path: { workspaceId, id: locationId },
        body: { autoReplyConfig: undefined },
      });
    },
    onSuccess: (_, locationId) => {
      queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root });
      queryClient.invalidateQueries({
        queryKey: locationsQueryKeys.detail(workspaceId, locationId),
      });
      toast.success(d.removeOverrideSuccess);
    },
    onError: () => toast.error(d.removeOverrideError),
  });

  return (
    <div className="flex flex-col gap-2">
      <SettingsGroupLabel>{d.overridesTitle}</SettingsGroupLabel>
      <SetupOverrideList
        locations={locations}
        isOverride={hasAutoReplyOverride}
        initialLocationId={initialLocationId}
        labels={{
          empty: d.noOverrides,
          add: d.addOverride,
          remove: d.removeOverride,
          overrideTag: d.overrideTag,
          location: d.locationLabel,
          locationPlaceholder: d.locationPlaceholder,
          continue: d.continueOverride,
          cancel: d.cancel,
        }}
        renderEditor={(loc) => (
          <ReputationAutoReplySettings
            workspaceId={workspaceId}
            scope="location"
            locationId={loc.id}
            compact
            grouped
            dict={settingsDict}
          />
        )}
        onRemove={(id) => removeMutation.mutate(id)}
        removing={removeMutation.isPending}
      />
    </div>
  );
}
